import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { Nullable } from "primereact/ts-helpers";
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { InputNumberChangeEvent } from 'primereact/inputnumber'
import { Tooltip } from "primereact/tooltip";

import NoDataCreated from "../../../../../Components/NoDataCreated";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import TextInput from "../../../../../Components/TextInput";
import DatePicker from "../../../../../Components/DatePicker";
import NumberInput from "../../../../../Components/NumberInput";
import FileInput from "../../../../../Components/FileInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { goToTop } from "../../../../../Components/goToTop";

import { checkFileOthers, checkNumber, checkText } from "../../../../../Utils/Validator";
import { paymentTrackerDocumentCreateAPI, paymentTrackerDocumentDeleteAPI, paymentTrackerDocumentGetDataApi, paymentTrackerDocumentUpdateAPI } from "../../../../../Api/Project/DefaultServices/PaymentTrackerApi";
import { PaymentTrackerServiceDocument } from "../../../../../Controllers/Types/Project/DefaultService/PaymentTrackerInterface";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { getAppCurrencyType } from "../../../../../Utils/Common";

interface Status {
    name: string;
    colorCode: string;
}

const PaymentTrackerService: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const today = new Date();
    const location = useLocation();
    const projectData = location.state?.projectData;
    const paymentTrackerId = location.state?.paymentTrackerId;
    const paymentTrackerServiceName = location.state?.paymentTrackerService;
    const currencySymbol = getAppCurrencyType();

    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const isShared: boolean = projectData.can_edit === false ? true : false;
    const [updateHasChanges, setUpdateHasChanges] = useState<boolean>(false);

    const [openMenuId, setOpenMenuId] = useState<number | null>(null);
    const menuRefs = useRef<Record<number, Menu | null>>({});
    const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    const statuselabels: Status[] = [
        {
            name: "Paid",
            colorCode: '#B9F6CA'
        },
        {
            name: "Unpaid",
            colorCode: '#fad6d6'
        },
        {
            name: "Pending",
            colorCode: '#FFE2B2'
        }
    ];

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loading, setLoading] = useState<boolean>(false);

    const [fileUrl, setFileUrl] = useState<string | null>(null);
    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false);

    const [statuses] = useState<string[]>(['Paid', 'Unpaid', 'Pending']);
    const [paymetTrackerDocumentData, setPaymetTrackerDocumentData] = useState<PaymentTrackerServiceDocument[]>([]);
    const [selectedDocData, setSelectedDocData] = useState<PaymentTrackerServiceDocument | null>(null);

    /* ***************************** form variables ****************************** */
    const [invoiceNumber, setInvoiceNumber] = useState<string>('');
    const [amount, setAmount] = useState<number | null>(null);
    const [invoiceDate, setInvoiceDate] = useState<Nullable<Date>>(null);
    const [dueDate, setDueDate] = useState<Nullable<Date>>(null);
    const [status, setStatus] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [fileName, setFileName] = useState<string>('');

    /* Error message */
    const [invoiceNumberError, setInvoiceNumberError] = useState<string>('');
    const [amountError, setAmountError] = useState<string>('');
    const [invoiceDateError, setInvoiceDateError] = useState<string>('');
    const [dueDateError, setDueDateError] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [fileError, setFileError] = useState<string>('');
    /* *************************************************************************** */

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_progress_options = useCanEdit('Payment Tracker', 'progress_options');
    const isPermission_add_more_details = useCanEdit('Payment Tracker', 'add_more_details');
    const isPermission_edit_more_detail = useCanEdit('Payment Tracker', 'edit_more_detail');
    const isPermission_delete_more_detail = useCanEdit('Payment Tracker', 'delete_more_detail');

    /* *************************** For table action menu ************************** */
    const tableActionMenuItems: MenuItem[] = [
        {
            items: [
                ...(isPermission_edit_more_detail
                    ? [{
                        label: 'Edit',
                        command: () => {
                            if (selectedDocData !== null) {
                                handleEditDocument(selectedDocData);
                            }
                        }
                    },]
                    : []
                ),

                ...(isPermission_delete_more_detail
                    ? [{
                        label: 'Delete',
                        command: () => {
                            if (selectedDocData !== null) {
                                handleDeletedocumentData(selectedDocData?.id);
                            }
                        }
                    },]
                    : []
                )
            ]
        }];

    const tableActionMenuToggle = (e: React.MouseEvent<HTMLButtonElement>, data: PaymentTrackerServiceDocument) => {
        e.stopPropagation();
        setSelectedDocData(data);

        if (openMenuId === data?.id) {
            menuRefs.current[data?.id]?.hide(e);
            setOpenMenuId(null);
        } else {
            if (openMenuId !== null && menuRefs.current[openMenuId]) {
                menuRefs.current[openMenuId]?.hide(e);
            }
            menuRefs.current[data?.id!]?.toggle(e);
            setOpenMenuId(data?.id!);
        }
    }
    /* *************************************************************************** */


    /* ****************************** for document ******************************* */
    const handleAddDocument = () => {
        setDataState('Add');
        setShowDocumentModal(true);
    }

    const handleClearFields = () => {
        setInvoiceNumber('');
        setAmount(null);
        setInvoiceDate(null);
        setDueDate(null);
        setStatus('');
        setFile(null);
        setFileName('');

        setSelectedDocData(null);
    }

    const handleClearErrors = () => {
        setInvoiceNumberError('');
        setAmountError('');
        setInvoiceDateError('');
        setDueDateError('');
        setStatusError('');
        setFileError('');
    }


    useEffect(() => {
        paymentTrackerDocumentDataGet();
    }, [paymentTrackerId]);


    /* *************************************** payment tracker service data *********************************** */
    const paymentTrackerDocumentDataGet = async () => {
        if (paymentTrackerId) {
            setDataLoading(true);
            const data = await paymentTrackerDocumentGetDataApi(Number(paymentTrackerId));
            if (data.status) {
                setPaymetTrackerDocumentData(data.data || []);
                setDataLoading(false);
                setInitialDataLoading(false);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} data.`);
            }
        }
    }
    /* *************************************** payment tracker service data *********************************** */
    useEffect(() => {
        checkText(invoiceNumber, false, setInvoiceNumberError);
    }, [invoiceNumber]);

    useEffect(() => {
        checkNumber(amount!, false, setAmountError);
    }, [amount]);

    useEffect(() => {
        checkText(formatDate(invoiceDate!), false, setInvoiceDateError);
    }, [invoiceDate]);

    useEffect(() => {
        checkText(formatDate(dueDate!), false, setInvoiceDateError);
    }, [dueDate]);

    useEffect(() => {
        checkText(status, false, setStatusError);
    }, [status]);

    useEffect(() => {
        checkFileOthers(file!, false, setFileError);
    }, [file]);


    function checkCredentials(invoiceDateOnly: string, dueDateOnly: string): boolean {
        const isInvoiceNumberValid = checkText(invoiceNumber, true, setInvoiceNumberError);
        const isAmountValid = checkNumber(amount!, true, setAmountError);
        const isInvoiceDateValid = checkText(invoiceDateOnly, false, setInvoiceDateError);
        const isDueDateOnlyValid = checkText(dueDateOnly, false, setDueDateError);
        const isStatusValid = checkText(status, true, setStatusError);
        const isFileValid = checkFileOthers(file!, false, setFileError);

        return isInvoiceNumberValid && isAmountValid && isInvoiceDateValid && isDueDateOnlyValid && isStatusValid && isFileValid;
    }


    /* ************************************** create ***************************************************  */
    // handle api error
    function showAPIError(keyName: string, error: string): void {
        if (keyName === "invoice_no") {
            setInvoiceNumberError(error);
        }

        if (keyName === "amount") {
            setAmountError(error);
        }

        if (keyName === "invoice_date") {
            setInvoiceDateError(error);
        }

        if (keyName === "due_date") {
            setDueDateError(error);
        }

        if (keyName === "status") {
            setStatusError(error);
        }

        if (keyName === "path") {
            setFileError(error);
        }
    }

    const handleCreateDocumentDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        const invoiceDateOnly = formatDate(invoiceDate || null);
        const dueDateOnly = formatDate(dueDate || null);

        if (checkCredentials(invoiceDateOnly, dueDateOnly)) {
            setLoading(true);
            const data = await paymentTrackerDocumentCreateAPI(
                Number(paymentTrackerId),
                invoiceNumber, amount || 0,
                invoiceDateOnly,
                dueDateOnly,
                status,
                file!
            );
            setLoading(false);

            if (data.statusCode === 200) {
                paymentTrackerDocumentDataGet();
                handleCloseDocumentModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document details added successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIError(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error creating document details',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ************************************** create ***************************************************  */

    /* ************************************** Update ***************************************************  */
    useEffect(() => {
        if (selectedDocData) {
            if (invoiceNumber !== selectedDocData?.invoice_no) {
                setUpdateHasChanges(true);
            } else if (amount !== selectedDocData?.amount) {
                setUpdateHasChanges(true);
            } else if (formatDate(invoiceDate!) !== selectedDocData?.invoice_date) {
                setUpdateHasChanges(true);
            } else if (formatDate(dueDate!) !== selectedDocData?.due_date) {
                setUpdateHasChanges(true);
            } else if (status !== selectedDocData?.status) {
                setUpdateHasChanges(true);
            } else if (file !== null) {
                setUpdateHasChanges(true);
            }
            else {
                setUpdateHasChanges(false);
            }
        }
    }, [selectedDocData, invoiceNumber, amount, invoiceDate, dueDate, status, file]);

    const handleUpdateDocumentDetail = async (e: React.FormEvent) => {
        e.preventDefault();

        const invoiceDateOnly = formatDate(invoiceDate || null);
        const dueDateOnly = formatDate(dueDate || null);

        if (checkCredentials(invoiceDateOnly, dueDateOnly)) {
            setLoading(true);
            const data = await paymentTrackerDocumentUpdateAPI(
                Number(selectedDocData?.id!),
                invoiceNumber,
                amount || 0,
                invoiceDateOnly,
                dueDateOnly,
                status,
                file!
            );

            setLoading(false);

            if (data.statusCode === 200) {
                paymentTrackerDocumentDataGet();
                handleCloseDocumentModal();

                if (toast.current) {
                    toast.current?.show({
                        severity: "success",
                        summary: "Success",
                        detail: `Document details updated successfully.`,
                        life: 3000,
                    });
                }
            } else if (data.statusCode === 422) {
                Object.entries(data.errors ?? []).map(([key, value], index) =>
                    showAPIError(key, value[0])
                );
            } else {
                if (toast.current) {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Failed',
                        detail: 'There was an error updating document details',
                        life: 3000
                    });
                }
            }
        }
    }
    /* ************************************** Update ***************************************************  */


    /* ************************************** Delete ***************************************************  */
    const handleDeletedocumentData = (docId: number) => {
        setOpenMenuId(null);
        confirmDialog({
            message: 'Are you sure you want to delete this document data?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteDocumentData(docId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deleteDocumentData = async (docId: number) => {
        const data = await paymentTrackerDocumentDeleteAPI(docId);

        if (data.statusCode === 200) {
            paymentTrackerDocumentDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Document data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting document data.',
                    life: 3000
                });
            }
        }
    }
    /* ************************************** Delete ***************************************************  */


    const handleEditDocument = (data: PaymentTrackerServiceDocument) => {
        setDataState('Edit');
        setShowDocumentModal(true);

        setOpenMenuId(null);
        setInvoiceNumber(data.invoice_no);
        setAmount(data.amount);
        setInvoiceDate(parseStringToDate(data?.invoice_date || ''));
        setDueDate(parseStringToDate(data?.due_date || ''));
        setStatus(data.status);
    }

    const handleCloseDocumentModal = () => {
        setShowDocumentModal(false);
        handleClearFields();
        handleClearErrors();
    }

    /* Modal header */
    const documentModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                {dataState}&nbsp;document detail
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDocumentModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    );

    /* Modal footer */
    const documentModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDocumentModal}
            />

            <Button
                label={`${loading ? 'Processing' : dataState === 'Add' ? 'Save' : 'Update'}`}
                onClick={dataState === 'Add' ? handleCreateDocumentDetail : handleUpdateDocumentDetail}
                loading={loading}
                className="custom_btn primary"
                disabled={dataState === 'Edit' && !updateHasChanges}
            />
        </div>
    );

    const handleAmountChange = (e: InputNumberChangeEvent) => {
        if (e.value != null) {
            setAmount(e.value);
        } else {
            setAmount(null);
        }
    };

    const handleStatusChange = (e: any) => {
        setStatus(e.value);
    };

    const handleFileUpload = (file: File) => {
        setFile(file);
        setFileName(file.name);
    };

    const handleClearFile = () => {
        setFile(null);
        setFileName('');
    };
    /* *************************************************************************** */


    /* ***************************** for table view ****************************** */
    const getRowClassName = (data: PaymentTrackerServiceDocument, options: { [key: string]: any }) => {
        const paymentStatus = data?.status;

        if (paymentStatus === 'Paid') {
            return 'project_data_row success';
        } else if (paymentStatus === 'Unpaid') {
            return 'project_data_row danger';
        } else if (paymentStatus === 'Pending') {
            return 'project_data_row warning';
        } else {
            return 'project_data_row';
        }
    };

    const daysCountBody = (rowData: PaymentTrackerServiceDocument) => {
        if (rowData.due_date === null) {
            return (
                <span className="count_label mt-1 mb-1"></span>
            )
        } else {
            const dataDueDate = new Date(rowData.due_date);
            const dataStatus = rowData.status;

            const timeDiff = dataDueDate.getTime() - today.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

            return (
                <div className="flex_center">
                    <Tooltip key={rowData?.id} target=".show_info" />

                    {dataStatus === 'Unpaid' ? (
                        <span
                            className="count_label show_info mt-1 mb-1"
                            style={{ backgroundColor: '#EF9A9A' }}
                            data-pr-tooltip={`${daysDiff} day${daysDiff > 1 ? 's' : ''} left`}
                            data-pr-position="top"
                        >
                            {daysDiff > 0 ? '+' : ''}{daysDiff}
                        </span>
                    ) : (
                        <span className="count_label mt-1 mb-1"></span>
                    )}
                </div>
            )
        }
    }

    const fileViewBody = (rowData: PaymentTrackerServiceDocument) => {
        return (
            <span className="d-flex justify-content-center">
                {rowData.path !== null ? (
                    <button className="doc_view_btn p-ripple"
                        onClick={() => handleViewDocument(rowData.path ?? '')}>
                        <Ripple />
                        <i className="bi bi-file-earmark-pdf"></i>
                    </button>
                ) : (
                    <span className="text-secondary text-center">--</span>
                )}
            </span>
        )
    }

    const tableActionBody = (rowData: PaymentTrackerServiceDocument) => {
        return (
            <React.Fragment>
                <Button
                    ref={(el) => (buttonRefs.current[rowData?.id as number] = el as HTMLButtonElement | null)}
                    key={`menu_toggle_button_${rowData?.id}`}
                    id={`menu_toggle_button_${rowData?.id}`}
                    icon="bi bi-three-dots-vertical"
                    className="is_btn action_trigger_btn p-ripple"
                    onClick={(e) => tableActionMenuToggle(e, rowData)}
                    aria-controls={`popup_menu_${rowData?.id}`}
                    aria-haspopup
                />

                <Menu
                    ref={(el) => (menuRefs.current[rowData?.id!] = el)}
                    id={`popup_menu_${rowData?.id}`}
                    key={`popup_menu_${rowData?.id}`}
                    model={tableActionMenuItems}
                    popup
                    appendTo={document.body}
                    className="service_action_menu"
                />
            </React.Fragment>
        )
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (openMenuId !== null) {
                const menuElement = menuRefs.current[openMenuId]?.getElement();
                const buttonElement = buttonRefs.current[openMenuId];

                if (
                    menuElement &&
                    !menuElement.contains(event.target as Node) &&
                    buttonElement &&
                    !buttonElement.contains(event.target as Node)
                ) {
                    const syntheticEvent = { stopPropagation: () => { }, preventDefault: () => { } } as React.SyntheticEvent;
                    menuRefs.current[openMenuId]?.hide(syntheticEvent);
                    setOpenMenuId(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openMenuId]);
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-2)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.feature_name}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {paymentTrackerServiceName}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="status_lable_container">
                                {paymetTrackerDocumentData?.length > 0 && (
                                    <div className="status_lable_area">
                                        {statuselabels?.map((status, index) => (
                                            <div key={index} className="status_lable">
                                                <span style={{ backgroundColor: status.colorCode }}></span>
                                                <h6>{status.name}</h6>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {isPermission_add_more_details && !isShared && (
                                <div className="page_action_area">
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDocument}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add document</span>
                                        <Ripple />
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="page_main_content_section">
                            {paymetTrackerDocumentData?.length > 0 ? (
                                <DataTable
                                    value={paymetTrackerDocumentData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    emptyMessage="No data available"
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    rowClassName={getRowClassName}
                                    loading={dataLoading}
                                    scrollable
                                >
                                    <Column
                                        header="Invoice no."
                                        headerClassName="text_purple"
                                        field="invoice_no"
                                        body={(rowData: PaymentTrackerServiceDocument) => (
                                            <span className="text_no_wrap text_bold">
                                                {rowData.invoice_no ? rowData.invoice_no : '--'}
                                            </span>
                                        )}
                                        style={{ width: '10%' }}
                                    ></Column>

                                    <Column
                                        header="Amount"
                                        headerClassName="text_purple"
                                        alignHeader={'right'}
                                        field="amount"
                                        body={(rowData) => (
                                            <span className="text_no_wrap text_semi_bold text_bold flex_end">
                                                {currencySymbol}&nbsp;{rowData.amount ? parseFloat(rowData.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '--'}
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    ></Column>

                                    <Column
                                        header="Invoice date"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        field="invoice_date"
                                        body={(rowData: PaymentTrackerServiceDocument) => (
                                            <span className="text_no_wrap flex_center">
                                                {rowData.invoice_date ? rowData.invoice_date : '---'}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Due date"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        field="due_date"
                                        body={(rowData: PaymentTrackerServiceDocument) => (
                                            <span className="text_no_wrap flex_center">
                                                {rowData.due_date ? rowData.due_date : '---'}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    <Column
                                        header="Status"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        field="status"
                                        body={(rowData: PaymentTrackerServiceDocument) => (
                                            <span className="text_no_wrap text_semi_bold flex_center">
                                                {rowData.status ? rowData.status : '---'}
                                            </span>
                                        )}
                                        style={{ width: '15%' }}
                                    >
                                    </Column>

                                    <Column
                                        body={daysCountBody}
                                        style={{ width: '5%' }}
                                    ></Column>

                                    <Column
                                        header="Doc"
                                        headerClassName="text_purple"
                                        alignHeader={"center"}
                                        body={fileViewBody}
                                        style={{ width: '10%' }}
                                    ></Column>

                                    {!isShared && (isPermission_edit_more_detail || isPermission_delete_more_detail) && (
                                        <Column
                                            body={tableActionBody}
                                            style={{ width: '5%' }}
                                            frozen
                                            alignFrozen="right"
                                        ></Column>
                                    )}
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No document added for ${paymentTrackerServiceName.toLowerCase()}. ${!isShared && isPermission_add_more_details ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div >


            {/* Document modal */}
            <Dialog
                header={documentModalHeader}
                footer={documentModalFooter}
                headerClassName="custom_modal_header"
                visible={showDocumentModal}
                className="custom_modal_dialog modal_dialog_md"
                onHide={handleCloseDocumentModal}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {/*Invoice no */}
                            <div className="col-12 col-sm-6">
                                <TextInput
                                    id="invoiceNumber"
                                    label="Invoice no."
                                    labelHtmlFor="invoiceNumber"
                                    required={true}
                                    value={invoiceNumber}
                                    placeholder="Invoice no."
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNumber(e.target.value)}
                                    error={invoiceNumberError}
                                />
                            </div>

                            {/* Amount */}
                            <div className="col-12 col-sm-6">
                                <NumberInput
                                    inputId="totalAmount"
                                    label="Amount"
                                    labelHtmlFor="totalAmount"
                                    placeholder="Amount"
                                    onChange={handleAmountChange}
                                    value={amount}
                                    required={true}
                                    minFractionDigits={2}
                                    maxFractionDigits={2}
                                    min={0}
                                    error={amountError}
                                    prefix={currencySymbol + ' '}
                                />
                            </div>

                            {/* Invoice date */}
                            <div className="col-12 col-sm-6">
                                <DatePicker
                                    id="invoiceDate"
                                    label="Invoice date"
                                    labelHtmlFor="invoiceDate"
                                    required={false}
                                    value={invoiceDate}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setInvoiceDate}
                                    error={invoiceDateError}
                                    showClear
                                    onClear={() => setInvoiceDate(null)}
                                />
                            </div>

                            {/* Due date */}
                            <div className="col-12 col-sm-6">
                                <DatePicker
                                    id="dueDate"
                                    label="Due date"
                                    labelHtmlFor="dueDate"
                                    required={false}
                                    value={dueDate}
                                    placeholder="dd/mm/yyyy"
                                    onChange={setDueDate}
                                    error={dueDateError}
                                    showClear
                                    onClear={() => setDueDate(null)}
                                />
                            </div>

                            {/* Status */}
                            <div className="col-12 col-sm-4">
                                <div className="page_form_group mb-sm-0">
                                    <label htmlFor="status" className="custom_form_label is_required">Status</label>
                                    <Dropdown
                                        id="status"
                                        value={status}
                                        options={statuses}
                                        onChange={handleStatusChange}
                                        placeholder="Select status"
                                        className="form_dropdown w-100"
                                        showClear
                                        disabled={!isPermission_progress_options}
                                    />

                                    {statusError &&
                                        <small className="form_error_msg">{statusError}</small>
                                    }
                                </div>
                            </div>

                            {/* File */}
                            <div className="col-12 col-sm-8">
                                <FileInput
                                    id="file"
                                    label="Upload file"
                                    labelHtmlFor="file"
                                    // required={dataState === 'Add' ? true : false}
                                    required={false}
                                    value={fileName}
                                    onUpload={handleFileUpload}
                                    onClear={handleClearFile}
                                    toast={toast}
                                    error={fileError}
                                    containerClassName="mb-0"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />
            {/*  */}
        </React.Fragment>
    )
}

export default PaymentTrackerService;